import { useState } from 'react';
import {
  Brain, Wrench, ChevronDown, ChevronRight, CheckCircle, XCircle, Loader2,
  FileText, Quote, AlertTriangle, Terminal, Code, Image as ImageIcon, Copy
} from 'lucide-react';

// ── 状态图标 ──────────────────────────────────────────────────────────────
function StatusIcon({ status }) {
  if (status === 'running') return <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />;
  if (status === 'done') return <CheckCircle className="w-3.5 h-3.5 text-green-500" />;
  if (status === 'failed') return <XCircle className="w-3.5 h-3.5 text-red-500" />;
  if (status === 'cancelled') return <XCircle className="w-3.5 h-3.5 text-gray-400" />;
  return null;
}

// ── 工具结果块：按内容类型自适应（doc 5.4）───────────────────────────────
function ToolResultBody({ resultType, content }) {
  if (resultType === 'log') {
    return (
      <div className="bg-gray-900 rounded-md p-2.5 font-mono text-xs leading-relaxed overflow-x-auto">
        {content.split('\n').map((line, i) => {
          const isErr = /error|fail|timeout/i.test(line);
          const isWarn = /warn/i.test(line);
          return <div key={i} className={isErr ? 'text-red-400' : isWarn ? 'text-yellow-400' : 'text-green-400'}>{line}</div>;
        })}
      </div>
    );
  }
  if (resultType === 'json') {
    return <pre className="bg-gray-50 border border-gray-200 rounded-md p-2.5 font-mono text-xs text-gray-700 overflow-x-auto">{content}</pre>;
  }
  if (resultType === 'image') {
    return (
      <div className="border border-gray-200 rounded-md p-3 bg-white flex items-center gap-2 text-xs text-gray-500">
        <ImageIcon className="w-4 h-4 text-gray-400" /> {content}（富媒体卡片）
      </div>
    );
  }
  if (resultType === 'error') {
    return <div className="bg-red-50 border border-red-200 rounded-md p-2.5 text-xs text-red-600">{content}</div>;
  }
  return <div className="text-xs text-gray-600 leading-relaxed">{content}</div>;
}

const RESULT_TYPE_META = {
  log: { icon: Terminal, label: '日志输出' },
  json: { icon: Code, label: '结构化 JSON' },
  text: { icon: FileText, label: '文本' },
  image: { icon: ImageIcon, label: '图片/图表' },
  error: { icon: AlertTriangle, label: '错误信息' },
};

// ── 步骤分组块（doc 5.1 步骤分组 + 8.1 折叠策略）─────────────────────────
function StepBlock({ block }) {
  // 进行中默认展开，历史默认折叠；失败默认展开
  const [expanded, setExpanded] = useState(block.status === 'running' || block.status === 'failed');
  const summary = `步骤 ${block.index} · ${block.action}`;

  return (
    <div className={`rounded-lg border ${block.status === 'failed' ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-gray-50/60'}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
        {expanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
        <StatusIcon status={block.status} />
        <span className="text-xs font-medium text-gray-700 flex-1">{summary}</span>
        {block.latency && <span className="text-xs text-gray-400">{block.latency}</span>}
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2">
          {block.children.map((child, i) => <Block key={i} block={child} nested />)}
        </div>
      )}
    </div>
  );
}

// ── 思考块（doc 5.1 思考过程：折叠卡片）──────────────────────────────────
function ThinkingBlock({ block, nested }) {
  const [expanded, setExpanded] = useState(block.status === 'running');
  return (
    <div className={`rounded-lg border border-amber-100 bg-amber-50/60 ${nested ? '' : 'mb-2'}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-2 px-3 py-1.5 text-left">
        {expanded ? <ChevronDown className="w-3 h-3 text-amber-400" /> : <ChevronRight className="w-3 h-3 text-amber-400" />}
        <Brain className="w-3.5 h-3.5 text-amber-500" />
        <span className="text-xs font-medium text-amber-700">{block.status === 'running' ? '思考中…' : `已思考 ${block.duration || '2'} 秒`}</span>
      </button>
      {expanded && <div className="px-3 pb-2 pl-8 text-xs text-amber-800/80 leading-relaxed">{block.content}</div>}
    </div>
  );
}

// ── 工具调用块 ────────────────────────────────────────────────────────────
function ToolCallBlock({ block }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-lg border border-blue-100 bg-blue-50/50">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-2 px-3 py-1.5 text-left">
        {expanded ? <ChevronDown className="w-3 h-3 text-blue-400" /> : <ChevronRight className="w-3 h-3 text-blue-400" />}
        <Wrench className="w-3.5 h-3.5 text-blue-500" />
        <span className="text-xs font-medium text-blue-700">调用工具</span>
        <span className="text-xs px-1.5 py-0.5 bg-white text-blue-600 rounded border border-blue-100">{block.tool}</span>
        <StatusIcon status={block.status} />
      </button>
      {expanded && (
        <div className="px-3 pb-2 pl-8">
          <div className="text-xs text-gray-400 mb-0.5">入参</div>
          <pre className="bg-gray-900 text-green-400 rounded p-2 text-xs font-mono overflow-x-auto">{block.args}</pre>
        </div>
      )}
    </div>
  );
}

// ── 工具结果块 ────────────────────────────────────────────────────────────
function ToolResultBlock({ block }) {
  const [expanded, setExpanded] = useState(false);
  const meta = RESULT_TYPE_META[block.resultType] || RESULT_TYPE_META.text;
  const Icon = meta.icon;
  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center gap-2 px-3 py-1.5 text-left">
        {expanded ? <ChevronDown className="w-3 h-3 text-gray-400" /> : <ChevronRight className="w-3 h-3 text-gray-400" />}
        <Icon className="w-3.5 h-3.5 text-gray-500" />
        <span className="text-xs font-medium text-gray-600">工具结果</span>
        <span className="text-xs text-gray-400">{meta.label}</span>
      </button>
      {expanded && <div className="px-3 pb-2 pl-8"><ToolResultBody resultType={block.resultType} content={block.content} /></div>}
    </div>
  );
}

// ── 最终回答块（Markdown 简化渲染 + 角标）─────────────────────────────────
function AnswerBlock({ block }) {
  return (
    <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-line py-1">
      {block.content}
    </div>
  );
}

// ── 引用来源块（doc 5.1 引用来源）─────────────────────────────────────────
function CitationsBlock({ block }) {
  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      <div className="flex items-center gap-1 text-xs text-gray-400 mb-1.5"><Quote className="w-3 h-3" /> 引用来源</div>
      <div className="space-y-1">
        {block.sources.map((s, i) => (
          <div key={i} className="flex items-start gap-1.5 text-xs text-gray-500">
            <span className="w-4 h-4 rounded bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0 text-[10px]">{i + 1}</span>
            <span><span className="text-gray-700">{s.doc}</span> · {s.snippet}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── 错误块（doc 5.1 错误提示）─────────────────────────────────────────────
function ErrorBlock({ block }) {
  return (
    <div className={`rounded-lg p-2.5 text-xs ${block.scope === 'fatal' ? 'bg-red-100 border border-red-300 text-red-700' : 'bg-red-50 border border-red-200 text-red-600'}`}>
      <div className="flex items-center gap-1.5 font-medium mb-0.5">
        <AlertTriangle className="w-3.5 h-3.5" />
        {block.scope === 'fatal' ? '整体失败' : '局部错误（不阻断后续）'}
      </div>
      {block.message}
    </div>
  );
}

// ── 块分发器 ──────────────────────────────────────────────────────────────
export function Block({ block, nested }) {
  switch (block.type) {
    case 'step': return <StepBlock block={block} />;
    case 'thinking': return <ThinkingBlock block={block} nested={nested} />;
    case 'tool_call': return <ToolCallBlock block={block} />;
    case 'tool_result': return <ToolResultBlock block={block} />;
    case 'answer': return <AnswerBlock block={block} />;
    case 'citations': return <CitationsBlock block={block} />;
    case 'error': return <ErrorBlock block={block} />;
    default:
      // doc 11 验收：未知块类型安全降级
      return <div className="text-xs text-gray-400 italic px-2 py-1">[不支持的内容块类型：{block.type}，已安全降级]</div>;
  }
}

// ── 整条回复渲染 ──────────────────────────────────────────────────────────
export function AgentReply({ blocks }) {
  return (
    <div className="space-y-2">
      {blocks.map((b, i) => <Block key={i} block={b} />)}
    </div>
  );
}
